import * as React from 'react'
import { cn } from '@/lib/utils'

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'destructive' | 'link'
  size?: 'default' | 'sm' | 'lg' | 'icon'
  asChild?: boolean
}

const variantStyles: Record<string, string> = {
  default: 'bg-[#0A1B4A] text-white hover:bg-[#0f2560] shadow-xs active:translate-y-px',
  outline: 'border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 active:translate-y-px',
  secondary: 'bg-slate-100 text-slate-900 hover:bg-slate-200 active:translate-y-px',
  ghost: 'hover:bg-slate-100 hover:text-slate-900 text-slate-700',
  destructive: 'bg-rose-600 text-white hover:bg-rose-700 shadow-xs active:translate-y-px',
  link: 'text-[#2563EB] underline-offset-4 hover:underline p-0 h-auto',
}

const sizeStyles: Record<string, string> = {
  default: 'h-11 px-5 py-2.5 text-sm',
  sm: 'h-9 px-3.5 text-xs',
  lg: 'h-12 px-8 text-base',
  icon: 'h-10 w-10 p-0',
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-full font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 select-none cursor-pointer',
          variantStyles[variant] || variantStyles.default,
          sizeStyles[size] || sizeStyles.default,
          className
        )}
        {...props}
      />
    )
  }
)

Button.displayName = 'Button'
